import { useEffect, useRef, useState } from "react"
import { ArrowLeft, Download, FileText } from "lucide-react"
import { csrfToken } from "../lib/rails"

export function ContextMarkdownEditor({ active, path, readOnly = false, onBack }) {
  const [draft, setDraft] = useState("")
  const [saved, setSaved] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const loadedPath = useRef(null)
  const dirty = saved !== null && draft !== saved

  useEffect(() => {
    if (!active || !path || loadedPath.current === path) return

    const controller = new AbortController()
    setLoading(true)
    setError(null)
    fetch(path, { cache: "no-store", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Could not load context.md.")
        return response.text()
      })
      .then((text) => {
        if (controller.signal.aborted) return
        loadedPath.current = path
        setSaved(text)
        setDraft(text)
      })
      .catch((failure) => { if (!controller.signal.aborted) setError(failure.message) })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [active, path, loadAttempt])

  useEffect(() => {
    if (!dirty) return

    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = "" }
    window.addEventListener("beforeunload", beforeUnload)
    return () => window.removeEventListener("beforeunload", beforeUnload)
  }, [dirty])

  async function save() {
    if (readOnly || !dirty || saving) return
    const text = draft
    setSaving(true)
    setError(null)
    try {
      const response = await fetch(path, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ repository_context: { markdown: text } })
      })
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error(payload.error || "Could not save context.md.")
      }
      setSaved(await response.text())
    } catch (failure) {
      setError(failure.message)
    } finally {
      setSaving(false)
    }
  }

  function download() {
    const url = URL.createObjectURL(new Blob([draft], { type: "text/markdown;charset=utf-8" }))
    const link = document.createElement("a")
    link.href = url
    link.download = "context.md"
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  if (!active) return null

  return (
    <section className="context-document" aria-labelledby="context-document-title">
      <header className="context-document-header">
        <button type="button" className="context-document-back" aria-label="Back to concepts" onClick={onBack}>
          <ArrowLeft aria-hidden="true" />
        </button>
        <FileText aria-hidden="true" className="context-document-icon" />
        <h1 id="context-document-title">context.md</h1>
        <div className="context-document-actions">
          <button type="button" className="context-document-download" aria-label="Download context.md" title="Download context.md" disabled={saved === null || loading} onClick={download}>
            <Download aria-hidden="true" />
          </button>
          {!readOnly ? <button type="button" className="context-document-save" disabled={!dirty || saving || loading} onClick={save}>{saving ? "Saving…" : "Save"}</button> : null}
        </div>
      </header>
      <div className="context-document-description">
        <p>{readOnly ? "Repository context." : "Your edits take effect on the next Context sync."}</p>
        <span role="status">{loading ? "Loading…" : saving ? "Saving…" : dirty ? "Unsaved changes" : saved !== null ? readOnly ? "Read only" : "Saved" : ""}</span>
      </div>
      {error ? (
        <div className="context-document-error" role="alert">
          {error}
          {saved === null ? <button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>Try again</button> : null}
        </div>
      ) : null}
      {!path ? <p className="context-document-error">Context is not available yet.</p> : null}
      <textarea
        aria-label="context.md"
        className="context-document-editor"
        readOnly={readOnly}
        disabled={saved === null || loading}
        spellCheck={false}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
            event.preventDefault()
            save()
          }
        }}
      />
    </section>
  )
}
